import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

const TRIGGER_LABELS = {
  soft: "Soft",
  hard: "Hard",
  predictive: "Predictive",
  spike: "Spike",
};

const ACTION_LABELS = {
  notify: "Notify",
  approval_gate: "Approval gate",
};

function stepSummary(step) {
  const trig = TRIGGER_LABELS[step.trigger_level] || step.trigger_level;
  const act = ACTION_LABELS[step.action] || step.action;
  return `${trig} → ${act}`;
}

const StepCard = ({ step, index, isSelected, onSelect, onMoveUp, onMoveDown, onDelete, canMoveUp, canMoveDown, canDelete, disabled }) => (
  <div
    role="button"
    tabIndex={0}
    aria-pressed={isSelected}
    className={`border rounded-card p-3 cursor-pointer transition-colors text-left ${
      isSelected ? "border-primary bg-primary-soft" : "border-border bg-card hover:border-text-3"
    }`}
    onClick={() => onSelect(step.id)}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(step.id);
      }
    }}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-xs text-text-3 shrink-0">#{index + 1}</span>
        <span className="text-sm font-medium text-text truncate">{stepSummary(step)}</span>
        {!step.delivery_enabled && (
          <span className="text-xs text-text-3 shrink-0">(disabled)</span>
        )}
      </div>
      {!disabled && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMoveUp(step.id); }}
            disabled={!canMoveUp}
            className="p-1 text-text-2 hover:text-text rounded-ctrl disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <FontAwesomeIcon icon={icon({ name: "chevron-up", style: "solid" })} className="text-xs" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMoveDown(step.id); }}
            disabled={!canMoveDown}
            className="p-1 text-text-2 hover:text-text rounded-ctrl disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <FontAwesomeIcon icon={icon({ name: "chevron-down", style: "solid" })} className="text-xs" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(step.id); }}
            disabled={!canDelete}
            className="p-1 text-danger hover:text-danger rounded-ctrl disabled:opacity-30 disabled:cursor-not-allowed"
            title="Remove step"
          >
            <FontAwesomeIcon icon={icon({ name: "trash", style: "regular" })} className="text-xs" />
          </button>
        </div>
      )}
    </div>
  </div>
);

StepCard.propTypes = {
  step: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canMoveUp: PropTypes.bool,
  canMoveDown: PropTypes.bool,
  canDelete: PropTypes.bool,
  disabled: PropTypes.bool,
};

const StepListPanel = ({ steps, selectedStepId, onSelectStep, onStepsChange, onAddStep, disabled }) => {
  const handleMoveUp = (stepId) => {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx <= 0) return;
    const next = [...steps];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onStepsChange(next);
  };

  const handleMoveDown = (stepId) => {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx < 0 || idx >= steps.length - 1) return;
    const next = [...steps];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onStepsChange(next);
  };

  const handleDelete = (stepId) => {
    const next = steps.filter((s) => s.id !== stepId);
    onStepsChange(next);
    if (selectedStepId === stepId && next.length > 0) {
      onSelectStep(next[0].id);
    } else if (next.length === 0) {
      onSelectStep(null);
    }
  };

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col">
      <div className="flex items-center justify-between pb-3 border-b border-border">
        <h3 className="text-sm font-semibold text-text">Escalation steps</h3>
        {!disabled && (
          <button
            type="button"
            onClick={onAddStep}
            className="text-primary hover:text-primary-hover text-sm font-medium cursor-pointer"
          >
            + Add step
          </button>
        )}
      </div>

      <div className="space-y-2 pt-3">
        {steps.length === 0 && (
          <div className="py-6 text-center text-sm text-text-2">
            No escalation steps yet.{" "}
            {!disabled && (
              <button
                type="button"
                onClick={onAddStep}
                className="text-primary hover:text-primary-hover font-medium cursor-pointer"
              >
                Add step
              </button>
            )}
          </div>
        )}
        {steps.map((step, idx) => (
          <StepCard
            key={step.id}
            step={step}
            index={idx}
            isSelected={step.id === selectedStepId}
            onSelect={onSelectStep}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            onDelete={handleDelete}
            canMoveUp={idx > 0}
            canMoveDown={idx < steps.length - 1}
            canDelete={true}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

StepListPanel.propTypes = {
  steps: PropTypes.array.isRequired,
  selectedStepId: PropTypes.string,
  onSelectStep: PropTypes.func.isRequired,
  onStepsChange: PropTypes.func.isRequired,
  onAddStep: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default StepListPanel;
